/**
 * useTherapistUnreadNotes Hook
 *
 * Provides real-time unread notification count for therapists.
 * Tracks when clients share notes, respond to prompts, or share worksheets.
 *
 * Features:
 * - Fetches combined unread count using get_therapist_unread_notes_count() RPC
 * - Real-time updates via Supabase subscriptions on task_notes AND worksheet_share_notifications tables
 * - Mark client notes as viewed functionality
 * - Auto-refetch on realtime events
 *
 * Usage:
 * const { unreadNotesCount, markClientAsViewed, isLoading } = useTherapistUnreadNotes();
 */

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const useTherapistUnreadNotes = () => {
  const { user, profile } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Only fetch if user is a therapist
  const isTherapist = profile?.role === "therapist";

  // Query for unread note count
  const {
    data: unreadNotesCount = 0,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["therapist-unread-notes-count", user?.id],
    queryFn: async () => {
      if (!user || !isTherapist) return 0;

      logger.info(
        "[useTherapistUnreadNotes] Fetching unread count for therapist:",
        user.id
      );

      const { data, error } = await supabase.rpc(
        "get_therapist_unread_notes_count",
        {
          target_therapist_id: user.id,
        }
      );

      if (error) {
        logger.error("[useTherapistUnreadNotes] Error fetching count:", error);
        return 0;
      }

      logger.info("[useTherapistUnreadNotes] Unread note count:", data);
      return data || 0;
    },
    enabled: !!user && isTherapist,
    staleTime: 60000, // 1 minute - reasonable for notification counts
    refetchOnWindowFocus: true, // Refetch when therapist returns to app
  });

  // Subscribe to realtime updates on BOTH task_notes and worksheet_share_notifications
  useEffect(() => {
    if (!user || !isTherapist) return;

    logger.info(
      "[useTherapistUnreadNotes] Setting up realtime subscriptions for therapist:",
      user.id
    );

    // Shared debouncing logic to prevent rapid refetches from multiple channels
    // FIXED: Removed unbounded Set growth - using only time-based debouncing
    let invalidationTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastInvalidationTime = 0;
    const DEBOUNCE_MS = 1000; // 1 second debounce

    const debouncedInvalidate = () => {
      const now = Date.now();

      // Skip if we invalidated recently (within debounce window)
      if (now - lastInvalidationTime < DEBOUNCE_MS) {
        logger.debug(
          "[useTherapistUnreadNotes] Debouncing invalidation",
          now - lastInvalidationTime,
          "ms since last"
        );
        return;
      }

      // Clear old timeout
      if (invalidationTimeout) {
        clearTimeout(invalidationTimeout);
      }

      // Schedule invalidation after brief delay to batch rapid events
      invalidationTimeout = setTimeout(() => {
        lastInvalidationTime = Date.now();
        logger.info(
          "[useTherapistUnreadNotes] Invalidating queries (debounced)"
        );
        queryClient.invalidateQueries({
          queryKey: ["therapist-unread-notes-count", user.id],
        });
      }, 300); // Wait 300ms for additional events
    };

    // Channel for task notes (existing)
    const notesChannel = supabase
      .channel(`therapist-notes:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT and UPDATE events
          schema: "public",
          table: "task_notes",
          filter: `is_shared=eq.true`, // Only track shared notes
        },
        (payload) => {
          logger.info(
            "[useTherapistUnreadNotes] Task note update received:",
            payload.eventType
          );
          debouncedInvalidate();
        }
      )
      .subscribe((status) => {
        logger.info(
          "[useTherapistUnreadNotes] Notes subscription status:",
          status
        );
      });

    // NEW: Channel for worksheet notifications
    const worksheetChannel = supabase
      .channel(`therapist-worksheets:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT and UPDATE events
          schema: "public",
          table: "worksheet_share_notifications",
          filter: `therapist_id=eq.${user.id}`, // Only track this therapist's notifications
        },
        (payload) => {
          logger.info(
            "[useTherapistUnreadNotes] Worksheet update received:",
            payload.eventType
          );
          debouncedInvalidate();
        }
      )
      .subscribe((status) => {
        logger.info(
          "[useTherapistUnreadNotes] Worksheet subscription status:",
          status
        );
      });

    return () => {
      logger.info("[useTherapistUnreadNotes] Cleaning up subscriptions");
      if (invalidationTimeout) {
        clearTimeout(invalidationTimeout);
      }
      notesChannel.unsubscribe();
      worksheetChannel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user object excluded, using user?.id
  }, [user?.id, isTherapist, queryClient]);

  // Function to mark a specific client's notes as viewed
  const markClientAsViewed = async (clientId: string) => {
    if (!user || !isTherapist) {
      logger.warn(
        "[useTherapistUnreadNotes] Cannot mark notes viewed: user not therapist"
      );
      return;
    }

    logger.info(
      "[useTherapistUnreadNotes] Marking client notes as viewed:",
      clientId
    );

    try {
      const { error } = await supabase.rpc("mark_client_notes_viewed", {
        target_client_id: clientId,
      });

      if (error) {
        throw error;
      }

      logger.info(
        "[useTherapistUnreadNotes] Successfully marked client notes as viewed"
      );

      // Invalidate query to update UI immediately
      queryClient.invalidateQueries(["therapist-unread-notes-count", user.id]);
    } catch (error) {
      logger.error(
        "[useTherapistUnreadNotes] Error marking notes viewed:",
        error
      );
      toast.error(t("common.error", "Fejl"));
      throw error;
    }
  };

  // Log errors
  if (error) {
    logger.error("[useTherapistUnreadNotes] Query error:", error);
  }

  return {
    unreadNotesCount,
    markClientAsViewed,
    isLoading,
  };
};
